import { useEffect, useState, type ReactElement } from 'react';
import { type Op, type Transcript } from '@schnitt/shared';
import { api, type TranscriptSummary } from '../api';
import { useEditor } from '../store';
import { useZeitText } from '../zeit';
import { Sheet, Slider } from './Sheet';

/**
 * Transcription. Two things people actually want from it: burned-in subtitles,
 * and a way to find the moment somebody said a particular sentence. Tapping a
 * line moves the playhead there.
 */
export function TranscriptSheet({ onClose }: { onClose: () => void }): ReactElement {
  const zeit = useZeitText();
  const project = useEditor((s) => s.project);
  const assets = useEditor((s) => s.assets);
  const timeline = useEditor((s) => s.timeline);
  const dispatch = useEditor((s) => s.dispatch);
  const setPlayhead = useEditor((s) => s.setPlayhead);
  const toast = useEditor((s) => s.toast);
  const role = useEditor((s) => s.role);

  const [summaries, setSummaries] = useState<TranscriptSummary[]>([]);
  const [openId, setOpenId] = useState<string | null>(timeline.subtitles.transcriptId);
  const [transcript, setTranscript] = useState<Transcript | null>(null);
  const [language, setLanguage] = useState('de');
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState('');

  const reload = async () => {
    if (!project) return;
    const { transcripts } = await api.transcripts(project.id);
    setSummaries(transcripts);
  };

  useEffect(() => {
    void reload();
    // Whatever is running finishes while the sheet is open; poll gently.
    const timer = setInterval(() => void reload(), 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  useEffect(() => {
    if (!openId) {
      setTranscript(null);
      return;
    }
    let cancelled = false;
    void api
      .transcript(openId)
      .then((res) => {
        if (!cancelled) setTranscript(res.transcript.data);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [openId]);

  const subtitles = timeline.subtitles;
  const setSubtitles = (patch: Record<string, unknown>) =>
    dispatch({ op: 'updateSubtitles', patch } as Op);
  const setStyle = (patch: Record<string, unknown>) =>
    dispatch({ op: 'updateSubtitles', patch: { style: { ...subtitles.style, ...patch } } } as Op);

  const start = async (assetId: string) => {
    setBusy(true);
    try {
      const { transcriptId, reused } = await api.startTranscription(assetId, language);
      setOpenId(transcriptId);
      await reload();
      toast('info', reused ? 'Für dieses Medium läuft bereits ein Transkript.' : 'Transkription gestartet.');
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Transkription konnte nicht gestartet werden.');
    } finally {
      setBusy(false);
    }
  };

  /**
   * Untertitel aus einer Datei (S21). Sie werden zu einer Abschrift wie jede
   * andere -- danach lässt sich am Text nachbessern, und der Weg hinaus steht
   * unten schon da.
   */
  const dateiLaden = async (datei: File) => {
    if (!project) return;
    setBusy(true);
    try {
      const inhalt = await datei.text();
      const { transcriptId, segmente } = await api.untertitelLaden(project.id, datei.name, inhalt, language);
      setOpenId(transcriptId);
      await reload();
      toast('success', `${segmente} Untertitel aus „${datei.name}“ geladen.`);
    } catch (err) {
      toast('error', err instanceof Error ? err.message : 'Die Datei konnte nicht gelesen werden.');
    } finally {
      setBusy(false);
    }
  };

  const useForSubtitles = (id: string) => {
    dispatch({ op: 'updateSubtitles', patch: { enabled: true, transcriptId: id } } as Op);
    toast('success', 'Untertitel werden beim Export eingebrannt.');
  };

  const saveEdit = async (index: number, text: string) => {
    if (!openId || !transcript) return;
    const next: Transcript = {
      ...transcript,
      segments: transcript.segments.map((s, i) => (i === index ? { ...s, text } : s)),
    };
    setTranscript(next);
    try {
      await api.saveTranscript(openId, next);
    } catch {
      toast('error', 'Die Korrektur konnte nicht gespeichert werden.');
    }
  };

  const transcribable = assets.filter((a) => a.status === 'ready' && a.hasAudio);
  // Was aus einer Datei kam, hat kein Medium -- daran ist es zu erkennen.
  const dateien = summaries.filter((t) => t.asset_id === null);
  const segments = (transcript?.segments ?? []).filter(
    (s) => query.trim() === '' || s.text.toLowerCase().includes(query.toLowerCase()),
  );

  return (
    <Sheet title="Transkript & Untertitel" onClose={onClose}>
      <label className="field">
        <span>Sprache</span>
        <select value={language} onChange={(e) => setLanguage(e.target.value)}>
          <option value="de">Deutsch</option>
          <option value="en">Englisch</option>
          <option value="fr">Französisch</option>
          <option value="tr">Türkisch</option>
          <option value="ar">Arabisch</option>
        </select>
      </label>

      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '0 0 8px' }}>Medien mit Ton</h3>
      <div className="list">
        {transcribable.length === 0 ? <p className="hint">Kein Medium mit Tonspur im Projekt.</p> : null}
        {transcribable.map((asset) => {
          const existing = summaries.find((s) => s.asset_id === asset.id);
          return (
            <div className="row" key={asset.id}>
              <div className="grow">
                <div className="title">{asset.name}</div>
                <div className="sub">
                  {existing ? <StatusBadge eintrag={existing} /> : <span className="badge">noch nicht</span>}{' '}
                  {existing?.error ?? ''}
                </div>
              </div>
              {existing?.status === 'ready' ? (
                <>
                  <button className="small" onClick={() => setOpenId(existing.id)}>
                    Öffnen
                  </button>
                  <button className="small primary" onClick={() => useForSubtitles(existing.id)} disabled={role === 'viewer'}>
                    Untertitel
                  </button>
                </>
              ) : (
                <button className="small primary" onClick={() => void start(asset.id)} disabled={busy || role === 'viewer'}>
                  Starten
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* Der Weg hinaus (S21): eine .srt neben dem Film. Steht ausserhalb des
          Blocks fürs Einbrennen, denn beides ist nicht dasselbe -- eingebrannt
          bekommt man nicht wieder weg, und die Plattformen wollen sie
          getrennt. Sichtbar, sobald überhaupt eine Abschrift gewählt ist. */}
      {subtitles.transcriptId ? (
        <div className="row" style={{ marginTop: 14 }}>
          <div className="grow">
            <div className="title">Untertitel als Datei neben den Film</div>
            <div className="sub">
              {subtitles.alsDatei
                ? 'Jeder fertige Film bekommt eine .srt daneben, zum Ab- und Anschalten beim Zuschauer.'
                : 'Bisher nur eingebrannt — YouTube und Instagram wollen sie als eigene Datei.'}
            </div>
          </div>
          <button
            className={`small ${subtitles.alsDatei ? '' : 'primary'}`}
            data-untertitel-als-datei={String(subtitles.alsDatei)}
            onClick={() => setSubtitles({ alsDatei: !subtitles.alsDatei })}
            disabled={role === 'viewer'}
          >
            {subtitles.alsDatei ? 'Aus' : 'An'}
          </button>
        </div>
      ) : null}

      {/* Untertitel, die nicht aus einem Medium kommen (S21). Sie stehen in
          einer eigenen Liste: die darüber ist „welches Medium wird
          abgeschrieben“, und eine Datei ist kein Medium. */}
      <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Aus einer Datei</h3>
      <div className="list" data-untertitel-dateien={String(dateien.length)}>
        {dateien.map((eintrag) => (
          <div className="row" key={eintrag.id}>
            <div className="grow">
              <div className="title">{eintrag.asset_name ?? 'Untertitel'}</div>
              <div className="sub">{eintrag.language}</div>
            </div>
            <button className="small" onClick={() => setOpenId(eintrag.id)}>
              Öffnen
            </button>
            <button
              className="small primary"
              onClick={() => useForSubtitles(eintrag.id)}
              disabled={role === 'viewer'}
            >
              Untertitel
            </button>
          </div>
        ))}
      </div>
      <label className={`chip ${busy || role === 'viewer' ? 'disabled' : ''}`} style={{ marginTop: 8 }}>
        SRT oder VTT laden
        <input
          type="file"
          accept=".srt,.vtt,text/vtt,application/x-subrip,text/plain"
          data-untertitel-datei
          style={{ display: 'none' }}
          disabled={busy || role === 'viewer'}
          onChange={(e) => {
            const datei = e.target.files?.[0];
            // Das Feld wird geleert, damit dieselbe Datei ein zweites Mal
            // gewählt werden kann -- sonst meldet der Browser keine Änderung.
            e.target.value = '';
            if (datei) void dateiLaden(datei);
          }}
        />
      </label>
      <p className="hint">
        Untertitel von aussen — aus einer Übersetzung oder einem anderen Programm. Sie lassen sich hier
        genauso nachbessern und unter den Film legen wie eine Abschrift.
      </p>

      {timeline.subtitles.enabled ? (
        <>
          <div className="row" style={{ marginTop: 14 }}>
            <div className="grow">
              <div className="title">Untertitel werden eingebrannt</div>
              <div className="sub">In der Vorschau so, wie sie im Export aussehen.</div>
            </div>
            <button
              className="small"
              onClick={() => dispatch({ op: 'updateSubtitles', patch: { enabled: false } } as Op)}
              disabled={role === 'viewer'}
            >
              Aus
            </button>
          </div>

          {/* The look was only editable by hand in the work file before, so
              everyone got the same grey-on-video default. */}
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>Aussehen</h3>
          <Slider
            label="Schriftgröße"
            value={subtitles.style.size}
            min={0.02}
            max={0.14}
            step={0.005}
            format={(v) => `${Math.round(v * 100)}%`}
            onChange={(size) => setStyle({ size })}
          />
          <Slider
            label="Höhe im Bild"
            value={subtitles.y}
            min={0.4}
            max={0.95}
            step={0.01}
            format={(v) => `${Math.round(v * 100)}%`}
            onChange={(y) => setSubtitles({ y })}
          />
          <Slider
            label="Zeilenlänge"
            value={subtitles.maxLineLength}
            min={16}
            max={60}
            step={1}
            format={(v) => `${v} Zeichen`}
            onChange={(maxLineLength) => setSubtitles({ maxLineLength: Math.round(maxLineLength) })}
          />
          <div className="slider-row">
            <label>Farbe</label>
            <input
              type="color"
              value={subtitles.style.color}
              onChange={(e) => setStyle({ color: e.target.value })}
              disabled={role === 'viewer'}
            />
          </div>
          <div className="chips" style={{ marginTop: 8 }}>
            <button
              className={`chip ${subtitles.style.bold ? 'active' : ''}`}
              onClick={() => setStyle({ bold: !subtitles.style.bold })}
              disabled={role === 'viewer'}
            >
              Fett
            </button>
            <button
              className={`chip ${subtitles.style.uppercase ? 'active' : ''}`}
              onClick={() => setStyle({ uppercase: !subtitles.style.uppercase })}
              disabled={role === 'viewer'}
            >
              GROSS
            </button>
            <button
              className={`chip ${subtitles.style.boxColor ? 'active' : ''}`}
              onClick={() => setStyle({ boxColor: subtitles.style.boxColor ? null : '#000000' })}
              disabled={role === 'viewer'}
            >
              Hinterlegt
            </button>
            <button
              className={`chip ${subtitles.style.outlineColor ? 'active' : ''}`}
              onClick={() => setStyle({ outlineColor: subtitles.style.outlineColor ? null : '#000000' })}
              disabled={role === 'viewer'}
            >
              Kontur
            </button>
          </div>
          {subtitles.style.boxColor ? (
            <div className="slider-row">
              <label>Hinterlegung</label>
              <input
                type="color"
                value={subtitles.style.boxColor}
                onChange={(e) => setStyle({ boxColor: e.target.value })}
                disabled={role === 'viewer'}
              />
            </div>
          ) : null}
          {subtitles.style.outlineColor && !subtitles.style.boxColor ? (
            <>
              <div className="slider-row">
                <label>Konturfarbe</label>
                <input
                  type="color"
                  value={subtitles.style.outlineColor}
                  onChange={(e) => setStyle({ outlineColor: e.target.value })}
                  disabled={role === 'viewer'}
                />
              </div>
              <Slider
                label="Konturstärke"
                value={subtitles.style.outlineWidth}
                min={0.02}
                max={0.3}
                step={0.01}
                format={(v) => `${Math.round(v * 100)}%`}
                onChange={(outlineWidth) => setStyle({ outlineWidth })}
              />
            </>
          ) : subtitles.style.outlineColor ? (
            <p className="hint">Hinterlegung oder Kontur, nicht beides.</p>
          ) : null}
        </>
      ) : null}

      {transcript ? (
        <>
          <h3 style={{ fontSize: 13, color: 'var(--muted)', margin: '18px 0 8px' }}>
            Text ({transcript.segments.length} Abschnitte)
          </h3>
          <label className="field">
            <span>Suchen</span>
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Wort oder Satz" />
          </label>
          <div className="list">
            {segments.map((segment) => {
              const index = transcript.segments.indexOf(segment);
              return (
                <div className="row" key={segment.id} style={{ alignItems: 'flex-start' }}>
                  <button className="small ghost" onClick={() => setPlayhead(segment.start)} style={{ flex: '0 0 auto' }}>
                    {zeit(segment.start)}
                  </button>
                  <textarea
                    className="grow"
                    style={{ minHeight: 44, background: 'transparent', border: 'none', padding: 4 }}
                    defaultValue={segment.text}
                    onBlur={(e) => void saveEdit(index, e.target.value)}
                    readOnly={role === 'viewer'}
                  />
                </div>
              );
            })}
          </div>
          {openId ? (
            <div className="chips" style={{ marginTop: 12 }}>
              <a className="chip" href={`/api/transcripts/${openId}/export.srt`} download>
                SRT laden
              </a>
              <a className="chip" href={`/api/transcripts/${openId}/export.vtt`} download>
                VTT laden
              </a>
              <a className="chip" href={`/api/transcripts/${openId}/export.txt`} download>
                Nur Text
              </a>
            </div>
          ) : null}
        </>
      ) : null}
    </Sheet>
  );
}

function StatusBadge({ eintrag }: { eintrag: TranscriptSummary }): ReactElement {
  if (eintrag.status === 'ready') return <span className="badge ok">fertig</span>;
  if (eintrag.status === 'failed') return <span className="badge err">Fehler</span>;
  // 7.40c: „läuft…“ sah beliebig lange gleich aus -- dieselbe Lücke wie bei
  // den Exporten, nur die dritte von drei Arten Auftrag.
  if (eintrag.stillstand) {
    return (
      <span className="badge err" data-stillstand={eintrag.stillstand.seit}>
        hängt seit {grobeMinuten(eintrag.stillstand.seit)}
      </span>
    );
  }
  return <span className="badge busy">läuft…</span>;
}

function grobeMinuten(sekunden: number): string {
  if (sekunden < 90) return 'gut eine Minute';
  return `etwa ${Math.round(sekunden / 60)} Minuten`;
}
